import { createLogic } from "redux-logic";
import * as History from "../../../../../../common-deprecated/History";
import { find, retailerAppsQueryParams } from "../../../../../../common-deprecated/utils";
import type { PopBasketHistoryType, PushHistoryType } from "../../actions/BasketActions";
import {
    PUSH_HISTORY,
    POP_BASKET_HISTORY,
    setWheel,
    setColour,
    setUpholstery,
    addExtra,
    removeExtra,
    addPack,
    removePack,
    removePackExtra,
    addPackExtra,
    setPrimaryAccentColour,
    setSecondaryAccentColour,
} from "../../actions/BasketActions";
import { PROPAGATED_QUERY_TYPES, NavStep } from "../../../../shared-logic/utils/Navigation";
import { minimizeBuildBasket } from "../../../utils/typedUtils";
import type { BuildAndBuyLogicType, BuildAndBuyDispatchType } from "../../store";
import type {
    ExtraType,
    ColourType,
    UpholsteryType,
    WheelType,
} from "../../../../../../common-deprecated/types/CommonTypes";
import type { BasketType } from "../../../../../shared-logic/types/BasketType";
import type { ItemsType } from "../../../../../../common-deprecated/redux/typeHelpers";
import type { CarType } from "../../../../../shared-logic/types/CarType";
import type { QueryType } from "../../../../../../common-deprecated/server/types";
import { findPackExtraParent } from "../../../../../shared-logic/utils/typedUtils";

const pushHistory = (createLogic as BuildAndBuyLogicType<PushHistoryType>)({
    type: PUSH_HISTORY,
    process({ getState }, _, done) {
        if (History.historyIsEnabled()) {
            const { basket, car, navigation, modelFilter, commonSettings, bnbSettings, utils } = getState();
            // Check if the path contains correct variable definition:
            // step/modelid/carid
            // when loading a toyota code the modelid and carid are not present
            let { path } = navigation;

            if (
                (navigation.step === NavStep.Customize || navigation.step === NavStep.Summary) &&
                (path.indexOf(car.id) === -1 || path.indexOf(car.model.id) === -1)
            ) {
                path = `${navigation.step}/${car.model.id}/${car.id}`;
            }

            const params: any = minimizeBuildBasket(basket, car, commonSettings);

            // FilterIndex should be added to history if it's in navigation or if we're on the modelFilter step.
            if (navigation.filterIndex || (navigation.step === NavStep.Model && bnbSettings.modelHasActiveFilters)) {
                // Make sure the filterIndex from modelFilter has priority over navigation.
                const filterIndex =
                    modelFilter.filteredGrades.length > 0
                        ? modelFilter.filteredGrades[0].filterIndex
                        : navigation.filterIndex;
                params.filterIndex = filterIndex;
            }

            // Add querystrings that need propagation.
            const allowedQueryTypes = [...PROPAGATED_QUERY_TYPES, ...retailerAppsQueryParams];
            (Object.keys(commonSettings.query) as (keyof QueryType)[])
                .filter((queryKey) => allowedQueryTypes.includes(queryKey))
                .forEach((key) => {
                    params[key] = commonSettings.query[key];
                });

            // Add default toggle option.
            params.financeOption = utils.financeOption;

            // TODO: add tradein quoteId to url (used to be here in tridion version)

            History.pushQueryParams({ path, ...params }, History.HistoryType.Build);
        }

        done();
    },
});

const checkColour = (
    colour: ColourType,
    c: any,
    items: ItemsType,
    car: CarType,
    dispatch: BuildAndBuyDispatchType,
    transaction: string | boolean,
) => {
    const { colours } = items;

    if (!c) {
        dispatch(setColour(car.colour, true, transaction));
    } else if (colour.id !== c) {
        const newColour = find(c, colours);
        if (newColour) {
            dispatch(setColour(newColour, true, transaction));
        }
    }
};

const checkUpholstery = (
    upholstery: UpholsteryType,
    u: any,
    items: ItemsType,
    car: CarType,
    dispatch: BuildAndBuyDispatchType,
    transaction: string | boolean,
) => {
    const { upholsteries } = items;

    if (!u) {
        dispatch(setUpholstery(car.upholstery, true, transaction));
    } else if (upholstery.id !== u) {
        const newUpholstery = find(u, upholsteries);
        if (newUpholstery) {
            dispatch(setUpholstery(newUpholstery, true, transaction));
        }
    }
};
const checkWheel = (
    wheel: WheelType,
    w: any,
    items: ItemsType,
    car: CarType,
    dispatch: BuildAndBuyDispatchType,
    transaction: string | boolean,
) => {
    const { wheels } = items;

    if (!w) {
        dispatch(setWheel(car.wheel, true, transaction));
    } else if (wheel.shortId !== w) {
        const newWheel = find(typeof w === "number" ? w : w.i, wheels);
        if (newWheel) {
            dispatch(setWheel(newWheel, true, transaction));
        }
    }
};

const checkExtras = (
    extras: ExtraType[],
    e: string[],
    items: ItemsType,
    dispatch: BuildAndBuyDispatchType,
    transaction: string | boolean,
) => {
    // remove old extras in basket
    extras
        .filter((extra) => !e || e.indexOf(extra.shortId) === -1)
        .filter((extra) => extra.userSelected)
        .forEach((extra) => {
            dispatch(removeExtra(extra, true, transaction));
        });

    if (!e) {
        return;
    }

    // Add missing extras
    e.filter((shortId: string) => !find(shortId, extras)).forEach((shortId: string) => {
        const extra = find(shortId, items.extras);
        if (extra) {
            dispatch(addExtra(extra, true, transaction));
        }
    });
};

const checkPacks = (
    { packs, packExtras, colour }: BasketType,
    { p, pe, pac, sac, c }: { p: string[]; pe: string[]; pac: string; sac: string; c: string },
    items: ItemsType,
    dispatch: BuildAndBuyDispatchType,
    transaction: string | boolean,
) => {
    // Remove packs that aren't present in the url.
    const selectedPacks = packs.filter((pack) => !p || !p.includes(pack.shortId));
    selectedPacks.forEach((pack) => {
        dispatch(removePack(pack, transaction));
    });

    // Remove old packExtras in basket.
    packExtras
        .filter((packExtra) => !pe || !pe.includes(packExtra.shortId))
        .forEach((packExtra) => {
            const parentPack = findPackExtraParent(packExtra.id, items.packs);
            dispatch(removePackExtra(parentPack, packExtra, transaction));
        });

    if (p) {
        // Add missing packs.
        p.filter((shortId: string) => !find(shortId, packs)).forEach((shortId: string) => {
            const pack = find(shortId, items.packs);
            if (pack) {
                selectedPacks.push(pack);
                dispatch(addPack(pack, [], [], false, false, true, transaction));
            }
        });
    }

    if (pe && selectedPacks.length > 0) {
        // Add missing packExtras.
        pe.filter((shortId: string) => !find(shortId, packExtras)).forEach((shortId: string) => {
            const parentPack = findPackExtraParent(shortId, selectedPacks);
            const parentPackEquipment = find(shortId, parentPack.equipment);

            if (parentPackEquipment) {
                dispatch(addPackExtra(parentPack, parentPackEquipment as ExtraType, [], true, transaction, false));
            }
        });
    }

    const extendedPack = selectedPacks.find((pack) => pack.extended);
    if (extendedPack) {
        if (pac) {
            const accentColour = extendedPack.extended?.accentColours[c || colour.id];

            if (accentColour) {
                const primaryAccentColour = find(pac, accentColour);
                if (primaryAccentColour) {
                    dispatch(setPrimaryAccentColour(extendedPack, primaryAccentColour, false, true, transaction));

                    if (sac) {
                        const secondaryAccentColour = find(sac, primaryAccentColour.accentColours);
                        if (secondaryAccentColour) {
                            dispatch(
                                setSecondaryAccentColour(extendedPack, secondaryAccentColour, false, true, transaction),
                            );
                        }
                    }
                }
            }
        }
    }
};

const popHistory = (createLogic as BuildAndBuyLogicType<PopBasketHistoryType>)({
    type: POP_BASKET_HISTORY,
    process({ getState, action }, dispatch, done) {
        const { basket, items, car } = getState();
        const { transaction } = action;

        //let loadedCar = action.tyCode?getState().loadedCar:null;

        // TODO Implement logic when going back to a tyCode in the URL. See also logic programmed against this in the BasketMiddleWare.
        checkColour(basket.colour, action.c, items, car, dispatch, transaction);
        checkUpholstery(basket.upholstery, action.u, items, car, dispatch, transaction);
        checkWheel(basket.wheel, action.w, items, car, dispatch, transaction);
        checkExtras(basket.extras, action.e, items, dispatch, transaction);
        checkPacks(basket, action as any, items, dispatch, transaction);

        done();
    },
});

export default [pushHistory, popHistory];
